/**
 * The service registry (ADR-0040, docs/SERVICES.md): services, their
 * repository bindings, environments, telemetry selectors and the incidents
 * attributed to them. Sixteen operations, one function each, paths checked
 * against the generated contract; re-exported from `operations.ts`.
 *
 * Visibility is the server's: a service the caller cannot see is a 404,
 * the same answer an identifier that names nothing gets, and a page reads
 * that as "not found or not visible" rather than guessing which. Lists keep
 * the `Page<T>` envelope because the registry is cursor-paged (DL-245 §5).
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Service = Schemas['Service'];
export type ServiceCreate = Schemas['ServiceCreate'];
export type ServiceUpdate = Schemas['ServiceUpdate'];
export type ServiceLifecycle = Schemas['ServiceLifecycle'];
export type ServiceRepository = Schemas['ServiceRepository'];
export type ServiceEnvironment = Schemas['ServiceEnvironment'];
export type TelemetrySelector = Schemas['TelemetrySelector'];
export type TelemetrySelectorCreate = Schemas['TelemetrySelectorCreate'];
export type TelemetrySelectorUpdate = Schemas['TelemetrySelectorUpdate'];
export type TelemetrySelectorSignal = TelemetrySelector['signal'];
export type ServiceTelemetryMatcher = Schemas['ServiceTelemetryMatcher'];
export type ServiceIncident = Schemas['Incident'];

/** The envelope, as `operations.ts` defines it; repeated to avoid a cycle. */
export interface ServicePage<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): ServicePage<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const orgPath = (orgId: string) => ({ org_id: orgId });
const servicePath = (orgId: string, serviceId: string) => ({
  org_id: orgId,
  service_id: serviceId,
});

/** A conditional write's version, as a query parameter; absent is unconditional. */
const ifVersion = (version: number | undefined) =>
  version === undefined ? {} : { if_version: version };

/* ====================================================================== *
 * Services
 * ====================================================================== */

export interface ServicesQuery {
  lifecycle?: ServiceLifecycle;
  repository_id?: string;
  team_id?: string;
  /** Prefix of a name or slug, ignoring case; at most 100 characters. */
  q?: string;
  limit?: number;
  cursor?: string;
}

export async function listServices(
  orgId: string,
  query: ServicesQuery = {},
): Promise<ServicePage<Service>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services', { params: { path: orgPath(orgId), query } }),
  );
  return paged<Service>(value);
}

export function createService(orgId: string, body: ServiceCreate): Promise<Service> {
  return expect(
    http.POST('/organizations/{org_id}/services', { params: { path: orgPath(orgId) }, body }),
  );
}

export function getService(orgId: string, serviceId: string): Promise<Service> {
  return expect(
    http.GET('/organizations/{org_id}/services/{service_id}', {
      params: { path: servicePath(orgId, serviceId) },
    }),
  );
}

export function updateService(
  orgId: string,
  serviceId: string,
  body: ServiceUpdate,
): Promise<Service> {
  return expect(
    http.PATCH('/organizations/{org_id}/services/{service_id}', {
      params: { path: servicePath(orgId, serviceId) },
      body,
    }),
  );
}

export function deleteService(orgId: string, serviceId: string, version?: number): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/services/{service_id}', {
      params: { path: servicePath(orgId, serviceId), query: ifVersion(version) },
    }),
  );
}

/* ====================================================================== *
 * Repository bindings
 * ====================================================================== */

export async function listServiceRepositories(
  orgId: string,
  serviceId: string,
  query: { limit?: number; cursor?: string } = {},
): Promise<ServicePage<ServiceRepository>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/repositories', {
      params: { path: servicePath(orgId, serviceId), query },
    }),
  );
  return paged<ServiceRepository>(value);
}

export function bindServiceRepository(
  orgId: string,
  serviceId: string,
  repoId: string,
  version?: number,
): Promise<ServiceRepository> {
  return expect(
    http.PUT('/organizations/{org_id}/services/{service_id}/repositories/{repo_id}', {
      params: {
        path: { ...servicePath(orgId, serviceId), repo_id: repoId },
        query: ifVersion(version),
      },
    }),
  );
}

export function unbindServiceRepository(
  orgId: string,
  serviceId: string,
  repoId: string,
  version?: number,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/services/{service_id}/repositories/{repo_id}', {
      params: {
        path: { ...servicePath(orgId, serviceId), repo_id: repoId },
        query: ifVersion(version),
      },
    }),
  );
}

/* ====================================================================== *
 * Environments (at most 20, unpaged)
 * ====================================================================== */

export async function listServiceEnvironments(
  orgId: string,
  serviceId: string,
): Promise<ServiceEnvironment[]> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/environments', {
      params: { path: servicePath(orgId, serviceId) },
    }),
  );
  return paged<ServiceEnvironment>(value).data;
}

export function putServiceEnvironment(
  orgId: string,
  serviceId: string,
  name: string,
  version?: number,
): Promise<ServiceEnvironment> {
  return expect(
    http.PUT('/organizations/{org_id}/services/{service_id}/environments/{name}', {
      params: { path: { ...servicePath(orgId, serviceId), name }, query: ifVersion(version) },
    }),
  );
}

export function deleteServiceEnvironment(
  orgId: string,
  serviceId: string,
  name: string,
  version?: number,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/services/{service_id}/environments/{name}', {
      params: { path: { ...servicePath(orgId, serviceId), name }, query: ifVersion(version) },
    }),
  );
}

/* ====================================================================== *
 * Telemetry selectors
 * ====================================================================== */

export async function listServiceTelemetrySelectors(
  orgId: string,
  serviceId: string,
  query: { limit?: number; cursor?: string } = {},
): Promise<ServicePage<TelemetrySelector>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/telemetry-selectors', {
      params: { path: servicePath(orgId, serviceId), query },
    }),
  );
  return paged<TelemetrySelector>(value);
}

export function createServiceTelemetrySelector(
  orgId: string,
  serviceId: string,
  body: TelemetrySelectorCreate,
): Promise<TelemetrySelector> {
  return expect(
    http.POST('/organizations/{org_id}/services/{service_id}/telemetry-selectors', {
      params: { path: servicePath(orgId, serviceId) },
      body,
    }),
  );
}

export function updateServiceTelemetrySelector(
  orgId: string,
  serviceId: string,
  selectorId: string,
  body: TelemetrySelectorUpdate,
): Promise<TelemetrySelector> {
  return expect(
    http.PATCH('/organizations/{org_id}/services/{service_id}/telemetry-selectors/{selector_id}', {
      params: { path: { ...servicePath(orgId, serviceId), selector_id: selectorId } },
      body,
    }),
  );
}

export function deleteServiceTelemetrySelector(
  orgId: string,
  serviceId: string,
  selectorId: string,
  version?: number,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/services/{service_id}/telemetry-selectors/{selector_id}', {
      params: {
        path: { ...servicePath(orgId, serviceId), selector_id: selectorId },
        query: ifVersion(version),
      },
    }),
  );
}

/* ====================================================================== *
 * Incidents attributed to a service
 * ====================================================================== */

export interface ServiceIncidentsQuery {
  state?: ServiceIncident['state'];
  severity?: ServiceIncident['severity'];
  live?: boolean;
  limit?: number;
  cursor?: string;
}

export async function listServiceIncidents(
  orgId: string,
  serviceId: string,
  query: ServiceIncidentsQuery = {},
): Promise<ServicePage<ServiceIncident>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/incidents', {
      params: { path: servicePath(orgId, serviceId), query },
    }),
  );
  return paged<ServiceIncident>(value);
}
